import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { useData, useTexts, useApi } from './data';
import tracker from './analytics/tracker';
import Header from './components/Header';
import Footer from './components/Footer';
import ConfirmModal from './components/ConfirmModal';
import { formatText } from './constants/texts';
import { RECAPTCHA_SITE_KEY } from './constants/config';

function ChangeRequestForm() {
  const { donorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { time } = useData();
  const TEXTS = useTexts();
  const api = useApi();
  const [submitStatus, setSubmitStatus] = useState(null);
  const [referenceId, setReferenceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [recordExists, setRecordExists] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [initialValues, setInitialValues] = useState({
    print_name: '',
    address: '',
    products: '',
  });
  const hasFetched = useRef(false);

  const validationSchema = Yup.object({
    print_name: Yup.string()
      .required(TEXTS.NAME_REQUIRED)
      .max(255, TEXTS.NAME_MAX_LENGTH),
    address: Yup.string().required(TEXTS.ADDRESS_REQUIRED),
  });

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (Number(time?.currentStatus) >= 2) {
      navigate('/');
      return;
    }

    const fetchExistingRequest = async () => {
      try {
        const response = await api.get(`/public/change-request/${donorId}`);
        if (response.status === 200 && response.data) {
          setRecordExists(true);
          setInitialValues({
            print_name: response.data.print_name || '',
            address: response.data.address || '',
            products: response.data.products || '',
          });
          if (response.data.reference_id) {
            setReferenceId(response.data.reference_id);
            setSubmitStatus('success');
          }
        } else {
          setRecordExists(false);
          const donorData = location.state || {};
          setInitialValues({
            print_name: donorData.donorNameTamil || '',
            address: donorData.fullAddress || '',
            products: donorData.products || '',
          });
        }
      } catch {
        setRecordExists(false);
        const donorData = location.state || {};
        setInitialValues({
          print_name: donorData.donorNameTamil || '',
          address: donorData.fullAddress || '',
          products: donorData.products || '',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchExistingRequest();
  }, [donorId, location.state, navigate, time, api]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [submitStatus]);

  useEffect(() => {
    if (submitStatus === 'success') {
      setCountdown(60);

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      const redirectTimer = setTimeout(() => {
        navigate('/');
      }, 60000);

      return () => {
        clearInterval(countdownInterval);
        clearTimeout(redirectTimer);
      };
    }
  }, [submitStatus, navigate]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referenceId).then(() => {
      alert(TEXTS.REFERENCE_COPIED);
    });
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setPendingValues({ values, setSubmitting });
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    const { values, setSubmitting } = pendingValues;
    setLoading(true);

    if (!executeRecaptcha) {
      alert(TEXTS.RECAPTCHA_ERROR);
      setSubmitting(false);
      setLoading(false);
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha('change_request_submit');
      let response;

      const payload = {
        ...values,
        recaptcha_token: recaptchaToken,
      };

      if (recordExists) {
        response = await api.put(`/public/change-request/${donorId}`, payload);
      } else {
        payload.donor_id = donorId;
        response = await api.post('/public/change-request', payload);
      }

      if (
        (response.status === 200 || response.status === 201) &&
        response.data.error === false
      ) {
        setSubmitStatus('success');
        setReferenceId(response.data.reference_id);
        setRecordExists(true);
        setInitialValues({
          print_name: values.print_name,
          address: values.address,
          products: values.products,
        });
        tracker.correctionSubmit(donorId, 'success');
      } else if (response.status === 429) {
        alert(TEXTS.RATE_LIMIT_ERROR);
        setSubmitting(false);
        tracker.correctionSubmit(donorId, 'rate_limited');
      } else {
        setSubmitStatus('error');
        tracker.correctionSubmit(donorId, 'error');
      }
    } catch {
      setSubmitStatus('error');
      tracker.correctionSubmit(donorId, 'error');
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
    if (pendingValues) {
      pendingValues.setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pb-2" data-component="ChangeRequest">
        <Header />
        <div className="container-fluid">
          <div className="card mt-3">
            <div className="card-body text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden"></span>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (submitStatus === 'success') {
    return (
      <div className="pb-2" data-component="ChangeRequest">
        <Header />
        <div className="container-fluid">
          <div className="card mt-3">
            <div className="card-body text-center p-4">
              <div className="text-success mb-3">
                <svg
                  width="64"
                  height="64"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                </svg>
              </div>
              <h4 className="text-success mb-3">{TEXTS.SUCCESS_TITLE}</h4>
              <p className="mb-2">{TEXTS.SUCCESS_MESSAGE}</p>
              <div className="mb-3">
                <div className="mb-2">
                  <strong className="d-block mb-2">
                    {TEXTS.REFERENCE_NUMBER}
                  </strong>
                  <span
                    className="badge bg-success px-2 py-2"
                    style={{ fontSize: '0.9rem' }}
                  >
                    {referenceId}
                  </span>
                </div>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={copyToClipboard}
                  title={TEXTS.COPY_REFERENCE}
                >
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    className="me-1"
                  >
                    <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z" />
                    <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z" />
                  </svg>
                  {TEXTS.COPY_REFERENCE}
                </button>
              </div>
              <p className="text-muted small mb-4">
                {formatText(TEXTS.AUTO_REDIRECT, { seconds: countdown })}
              </p>
              <div className="d-grid gap-2">
                <button
                  className="btn btn-warning"
                  onClick={() => setSubmitStatus(null)}
                >
                  Edit Request
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/')}
                >
                  {TEXTS.GO_HOME}
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (submitStatus === 'error') {
    return (
      <div className="pb-2" data-component="ChangeRequest">
        <Header />
        <div className="container-fluid">
          <div className="card mt-3">
            <div className="card-body text-center p-4">
              <div className="text-danger mb-3">
                <svg
                  width="64"
                  height="64"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                </svg>
              </div>
              <h4 className="text-danger mb-3">{TEXTS.ERROR_TITLE}</h4>
              <p className="mb-4">{TEXTS.ERROR_MESSAGE}</p>
              <p className="mb-4">
                {TEXTS.ERROR_CONTACT}{' '}
                <a href="tel:9886662366" className="text-primary">
                  9886662366
                </a>
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setSubmitStatus(null)}
              >
                {TEXTS.RETRY_BUTTON}
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pb-2" data-component="ChangeRequest">
      <Header />
      <div className="container-fluid">
        <div className="card mt-3 border-0 shadow-sm">
          <div className="card-body p-4">
            <h5 className="card-title mb-4 color fw-bold">
              {TEXTS.CHANGE_REQUEST_TITLE}
            </h5>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ isSubmitting, dirty }) => (
                <Form>
                  <div className="mb-3">
                    <label htmlFor="print_name" className="form-label">
                      {TEXTS.PRINT_NAME_LABEL}{' '}
                      <span className="text-danger">
                        {TEXTS.REQUIRED_FIELD}
                      </span>
                    </label>
                    <Field
                      as="textarea"
                      name="print_name"
                      id="print_name"
                      className="form-control"
                      rows="5"
                      placeholder={TEXTS.PRINT_NAME_PLACEHOLDER}
                    />
                    <ErrorMessage
                      name="print_name"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="address" className="form-label">
                      {TEXTS.ADDRESS_LABEL}{' '}
                      <span className="text-danger">
                        {TEXTS.REQUIRED_FIELD}
                      </span>
                    </label>
                    <Field
                      as="textarea"
                      name="address"
                      id="address"
                      className="form-control"
                      rows="7"
                      placeholder={TEXTS.ADDRESS_PLACEHOLDER}
                    />
                    <ErrorMessage
                      name="address"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>

                  {initialValues.products && (
                    <div className="mb-3">
                      <label htmlFor="products" className="form-label">
                        Products (Optional)
                      </label>
                      <Field
                        as="textarea"
                        name="products"
                        id="products"
                        className="form-control"
                        rows="3"
                        placeholder="Enter products if any"
                      />
                    </div>
                  )}

                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting || !dirty}
                    >
                      {isSubmitting
                        ? TEXTS.SUBMITTING_BUTTON
                        : TEXTS.SUBMIT_BUTTON}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate(-1)}
                      disabled={isSubmitting}
                    >
                      {TEXTS.CANCEL_BUTTON}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
      />
      <Footer />
    </div>
  );
}

export default ChangeRequestForm;

function ChangeRequest() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <ChangeRequestForm />
    </GoogleReCaptchaProvider>
  );
}

export { ChangeRequest };
